<script setup lang="ts">
import { ref, toRef, watch, computed } from 'vue';
import { IonIcon, IonInput, IonLabel } from '@ionic/vue';
import { useField, useIsFormValid } from 'vee-validate';
import { useI18n } from 'vue-i18n';

const props = defineProps({
	disabled: { type: Boolean, default: false },
	iconLeft: { type: String, default: '' },
	inputClass: { type: String, default: 'font-sourcesanspro-regular text-base' },
	label: { type: String },
	name: { type: String, required: true },
	manualSubmit: { type: Function },
	placeholder: { type: String, default: '' },
	step: { type: String as () => string | undefined },
	showErrorMessage: { type: Boolean, default: true },
	showInputSuccess: { type: Boolean, default: true },
	shouldHandleBlur: { type: Boolean, default: true },
	shouldValidate: { type: Boolean, default: true },
	successMessage: { type: String, default: '' },
	value: { type: String as () => string | number | null | undefined, default: null },
	wrapperClass: { type: String, default: '' },
});

const emit = defineEmits(['inputChanged', 'enterHit']);
const { t } = useI18n();
// use `toRef` to create reactive references to `name` prop which is passed to `useField`
// this is important because vee-validte needs to know if the field name changes
// https://vee-validate.logaretm.com/v4/guide/composition-api/caveats
const name = toRef(props, 'name');

// we don't provide any rules here because we are using form-level validation
// https://vee-validate.logaretm.com/v4/guide/validation#form-level-validation
const { value, errorMessage, handleChange, meta, setValue, setErrors } = useField(name, undefined, {
	initialValue: props.value,
});

const isFormValid = useIsFormValid();

const handleKeyDown = (event: KeyboardEvent) => {
	const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', ',', '.'];
	if (/^[0-9]$/.test(event.key)) return;
	if (allowed.includes(event.key)) return;

	event.preventDefault();
};

// Watch external prop.value → update field value
watch(
	() => props.value,
	(newVal) => {
		setValue(newVal ?? '');
	},
);

const handleInputChange = (event: any, enterHit: boolean) => {
	const inputValue = event.target?.value ?? '';
	const normalizedValue = inputValue.toString().replace(',', '.');

	if (normalizedValue !== '') {
		if (isNaN(normalizedValue)) {
			setErrors(t('general.field_not_number'));
		} else {
			setErrors('');
			emit('inputChanged', isFormValid, name.value, {
				detail: { value: normalizedValue },
			});

			if (props.shouldValidate) {
				if (props.shouldHandleBlur) {
					handleChange(event);
				} else {
					handleChange(event);
					setValue(normalizedValue);
				}
			}

			if (props.manualSubmit && isFormValid && enterHit) {
				props.manualSubmit();
			}

			if (enterHit && event.target?.blur) {
				event.target.blur();
			}
		}
	} else {
		setErrors(t('general.field_required'));
	}
};
</script>

<template>
	<div class="flex flex-col w-full">
		<div v-if="props.label" class="mb-2">
			<ion-label class="font-sourcesanspro-regular">{{ props.label }}</ion-label>
		</div>
		<div
			class="wrapper border-white-15"
			:class="[
				{
					'border-error': !!errorMessage && !!wrapperClass,
					'border-success': props.showInputSuccess && meta.valid && meta.dirty,
				},
				wrapperClass,
			]">
			<div class="flex w-full items-center px-6">
				<ion-icon :icon="props.iconLeft" class="mr-3" color="primary" v-if="props.iconLeft" slot="start"></ion-icon>
				<ion-input :class="props.inputClass" :name="props.name" :type="'text'" :disabled="props.disabled" :placeholder="props.placeholder" :step="props.step" :inputmode="'decimal'" :value="value" @keydown="handleKeyDown" @keydown.enter="(event) => handleInputChange(event, true)" @ionChange="(event) => handleInputChange(event, false)" @ionBlur="(event) => handleInputChange(event, false)"> </ion-input>
			</div>
		</div>
		<p class="mb-4 font-sourcesanspro-regular text-sm font-color-error" v-show="errorMessage && showErrorMessage">
			{{ errorMessage }}
		</p>
	</div>
</template>

<style lang="scss" scoped>
ion-label {
	color: rgb(255 255 255 / 0.5) !important;
	font-size: 12px !important;
}

div.wrapper {
	border-radius: 9999px;
	height: 52px;

	p {
		&::first-letter {
			text-transform: capitalize;
		}
	}

	ion-icon {
		cursor: pointer;
	}

	// .border-success input {
	//   background-color: var(--success-bg-color);
	//   color: var(--success-color);
	// }

	&.border-success {
		border: 1px solid var(--clientx-color-success) !important;

		ion-icon {
			color: var(--clientx-color-success);
		}
	}
}
</style>
